import { ApplicationConfig, inject, provideBrowserGlobalErrorListeners, provideAppInitializer } from '@angular/core';
import { provideRouter, withComponentInputBinding, withHashLocation, withViewTransitions } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { routes } from './app.routes';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { providePrimeNG } from 'primeng/config';
import { MessageService } from 'primeng/api';
import { MyPreset } from './themes/theme';
import { httpInterceptor } from '../services/mainServices/http-interceptor';
import { AuthService } from '../services/mainServices/auth';
import { SessionService } from '../services/mainServices/session';
import { UaePassService } from '../services/mainServices/uaepass-auth';
import { firstValueFrom } from 'rxjs';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { environment } from '../environment.dev';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideHttpClient(withInterceptors([httpInterceptor])),
    provideAppInitializer(async () => {
      const authService = inject(AuthService);
      const sessionService = inject(SessionService);
      const uaePassService = inject(UaePassService);

      // UAE Pass callback (?code=&state=): either log in, or link to the current account.
      const callback = uaePassService.processCallback();
      if (callback) {
        try {
          if (callback.intent === 'link') {
            // Keep the existing session; just mark the account verified/linked.
            const response = await firstValueFrom(authService.linkUaePass({ token: callback.code }));
            // The endpoint may answer with the user nested, bare, or not at all. What
            // matters is that the call resolved, so always persist the linked flag —
            // it is the only thing gating the import, and nothing re-fetches the
            // account until the next login.
            const user = response?.user ?? response;
            const isUserPayload = !!(user?.id ?? user?.objectId);
            sessionService.updateCurrentUser({
              ...(isUserPayload ? user : {}),
              linkedWithUaePass: true,
            });
            if (isUserPayload && user.sessionToken) sessionService.saveToken(user.sessionToken);
            window.location.hash = '#/main/properties';
          } else {
            const { user } = await firstValueFrom(authService.loginWithUaePass({ token: callback.code })) ?? {};
            if (user?.sessionToken) sessionService.saveSession(user, user.sessionToken);
            window.location.hash = '#/main/home';
          }
        } catch (error) {
          console.error('UAE Pass callback failed:', error);
          // Don't strand the user on the bare callback URL when the exchange fails.
          window.location.hash = callback.intent === 'link' ? '#/main/properties' : '#/auth/login';
        }
        return;
      }

      // Guest login (only if no existing session)
      const existingToken = localStorage.getItem('sessionToken');
      if (existingToken) {
        return;
      }

      const guestId = authService.generateGuestId();
      try {
        const response = await firstValueFrom(authService.loginAnonymous({ id: guestId }));
        if (response?.anonymousUser?.sessionToken) {
          localStorage.setItem('sessionToken', response.anonymousUser.sessionToken);
        }
      } catch (error) {
        console.error('Guest login initialization failed:', error);
      }
    }),
    provideRouter(routes, withHashLocation(), withViewTransitions(), withComponentInputBinding()),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideMessaging(() => getMessaging()),
    MessageService,
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: './i18n/',
        suffix: '.json',
      }),
      fallbackLang: 'en',
      lang: 'en',
    }),
    providePrimeNG({
      theme: {
        preset: MyPreset,
        options: {
          darkModeSelector: '.dark',
        },
      },
    }),
  ],
};
